import { Dispatch, SetStateAction, useRef, useState } from "react";

import { type BannerSource } from "@simplyk/common";
import { FieldValues, Path, PathValue, type UseFormReturn } from "react-hook-form";

import { CroppedFile } from "../../Crop/Crop";

// eslint-disable-next-line zeffy-linter-frontend/no-cross-layer-imports -- legacy cross-layer import (pre-rule); migrate when touched
import { type SquareImageEditorHandle } from "@/features/LiveFormEditor/components/SquareImageEditor/SquareImageEditor";

interface UseImageEditorParams<T extends FieldValues> {
  form: UseFormReturn<T>;
  name: Path<T>;
  isUploadingName: Path<T>;
  setFileUrl: Dispatch<SetStateAction<string | undefined | null>>;
  updateFileUrl: (image: string | undefined | null) => Promise<string | null>;
  fillImageMetadata: (url: string) => void;
  onImageSourceChange: ((source: BannerSource | null) => void) | undefined;
  setSelectImageModalOpen: Dispatch<SetStateAction<boolean>>;
}

export const useImageEditor = <T extends FieldValues>({
  form,
  name,
  isUploadingName,
  setFileUrl,
  updateFileUrl,
  fillImageMetadata,
  onImageSourceChange,
  setSelectImageModalOpen,
}: UseImageEditorParams<T>) => {
  const { setValue } = form;

  const [isCropOpen, setIsCropOpen] = useState(false);
  const [croppedFile, setCroppedFile] = useState<CroppedFile | undefined>(undefined);
  const [isSquareEditorOpen, setIsSquareEditorOpen] = useState(false);
  const [squareEditorImageUrl, setSquareEditorImageUrl] = useState<string | null>(null);

  const squareImageEditorRef = useRef<SquareImageEditorHandle | null>(null);
  const finalizeResolverRef = useRef<((url: string | null) => void) | null>(null);
  // Concurrent callers (step save, prize duplicate) share one upload; a second resolver would orphan the first.
  const pendingFinalizeRef = useRef<Promise<string | null> | null>(null);
  const isPreparingSquareEditorRef = useRef(false);

  const setSquareEditorPreparing = (isPreparing: boolean) => {
    if (isPreparingSquareEditorRef.current === isPreparing) {
      return;
    }
    isPreparingSquareEditorRef.current = isPreparing;
    setValue(isUploadingName, isPreparing as PathValue<T, Path<T>>);
  };

  const openCropEditor = ({ url, type }: CroppedFile) => {
    setCroppedFile({ url, type });
    fillImageMetadata(url);
    setIsCropOpen(true);
  };

  const openSquareEditor = (url: string) => {
    setSquareEditorImageUrl(url);
    setIsSquareEditorOpen(true);
    fillImageMetadata(url);
    setValue(name, url as PathValue<T, Path<T>>, { shouldDirty: true });
    setSquareEditorPreparing(true);
  };

  const closeCrop = () => {
    setIsCropOpen(false);
  };

  const confirmCrop = (image: string) => {
    updateFileUrl(image);
    setIsCropOpen(false);
    setSelectImageModalOpen(false);
  };

  const settlePendingFinalize = (url: string | null) => {
    finalizeResolverRef.current?.(url);
    finalizeResolverRef.current = null;
    pendingFinalizeRef.current = null;
  };

  const confirmSquareEditor = async (finalImageDataUrl: string) => {
    const uploadedUrl = await updateFileUrl(finalImageDataUrl);
    setIsSquareEditorOpen(false);
    setSquareEditorImageUrl(null);
    settlePendingFinalize(uploadedUrl);
  };

  const updateSquareEditorPreview = (previewDataUrl: string) => {
    setValue(name, previewDataUrl as PathValue<T, Path<T>>, { shouldDirty: true });
    setSquareEditorPreparing(false);
  };

  const replaceSquareEditor = () => {
    setSelectImageModalOpen(true);
  };

  const deleteSquareEditor = () => {
    setIsSquareEditorOpen(false);
    setSquareEditorImageUrl(null);
    setFileUrl(null);
    setValue(name, null as PathValue<T, Path<T>>, { shouldDirty: true });
    setSquareEditorPreparing(false);
    onImageSourceChange?.(null);
    settlePendingFinalize(null);
  };

  const finalize = async (): Promise<string | null> => {
    if (pendingFinalizeRef.current) {
      return pendingFinalizeRef.current;
    }
    if (!isSquareEditorOpen || !squareEditorImageUrl) {
      return null;
    }

    pendingFinalizeRef.current = new Promise((resolve) => {
      finalizeResolverRef.current = resolve;
      squareImageEditorRef.current?.confirm();
    });
    return pendingFinalizeRef.current;
  };

  const resetEditor = () => {
    setIsSquareEditorOpen(false);
    setSquareEditorImageUrl(null);
    setSquareEditorPreparing(false);
    settlePendingFinalize(null);
  };

  return {
    isCropOpen,
    croppedFile,
    setCroppedFile,
    isSquareEditorOpen,
    squareEditorImageUrl,
    squareImageEditorRef,
    openCropEditor,
    openSquareEditor,
    closeCrop,
    confirmCrop,
    confirmSquareEditor,
    updateSquareEditorPreview,
    replaceSquareEditor,
    deleteSquareEditor,
    finalize,
    resetEditor,
  };
};
